import { type FunctionComponent, useState, useEffect } from 'react'
import { NotificationRow } from '@retailer-platform/shared-components/src/tds'
import { trackEvent } from '@retailer-platform/dashboard/analytics'
import {
  getLink,
  useRenderTemplate,
  useRenderTitle,
  type NotificationTemplateType,
} from '../../templates/TemplateRegistry'
import { type NotificationSeverityEnum } from '../../api'
import { useMarkNotificationsAsRead } from '../../hooks/useMarkNotificationsAsRead'
import { useDeleteNotifications } from '../../hooks/useDeleteNotifications'
import { type BaseTemplateProps } from '../../templates/NotificationTemplate'

interface TemplateData extends BaseTemplateProps {
  [key: string]: any
}

export interface NotificationProps {
  id: string
  title: string
  source: string
  category: string
  isRead: boolean
  severity: NotificationSeverityEnum
  templateType: NotificationTemplateType
  templateData: TemplateData
  createdAt: string
  onCleared?: () => void
  refetch?: () => void
  feedSection?: string
  accountId?: string
}

export const NotificationItem: FunctionComponent<NotificationProps> = ({
  id,
  title,
  source,
  category,
  isRead: propIsRead,
  templateType,
  templateData,
  createdAt,
  onCleared,
  refetch,
  feedSection,
  accountId,
}) => {
  const [isRead, setIsRead] = useState(propIsRead)
  const { markAsRead } = useMarkNotificationsAsRead()
  const { deleteNotifications } = useDeleteNotifications()
  const content = useRenderTemplate(templateType, templateData)
  const link = getLink(templateType, templateData)
  const renderedTitle = useRenderTitle(templateType, templateData)

  // Update local state when prop changes
  useEffect(() => {
    setIsRead(propIsRead)
  }, [propIsRead])

  const handleMarkAsRead = async () => {
    trackEvent({
      id: 'notifications.drawer.notification.marked_read',
      description: 'User marked individual notification as read',
      data: {
        notificationId: id,
        notificationCategory: category,
        notificationSource: source,
        templateType: templateType,
        feedSection: feedSection || 'unknown',
        accountId: accountId || 'unknown',
        timestamp: new Date().toISOString(),
      },
    })
    const success = await markAsRead([id])
    if (success) {
      setIsRead(true)
      await refetch?.() // Refetch to update the cache
    }
  }

  const handleClear = async () => {
    trackEvent({
      id: 'notifications.drawer.notification.cleared',
      description: 'User cleared/dismissed a notification',
      data: {
        notificationId: id,
        notificationCategory: category,
        notificationSource: source,
        templateType: templateType,
        isRead: isRead,
        feedSection: feedSection || 'unknown',
        accountId: accountId || 'unknown',
        timestamp: new Date().toISOString(),
      },
    })
    const success = await deleteNotifications([id])
    if (success) {
      onCleared?.()
    }
  }

  return (
    <div data-testid={`notification-${id}`} data-created-at={createdAt}>
      <NotificationRow
        title={renderedTitle || title}
        isRead={isRead}
        source={source}
        category={category}
        timestamp={createdAt}
        href={link}
        onMarkAsRead={handleMarkAsRead}
        onClear={handleClear}
        content={content}
      />
    </div>
  )
}
